import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Broadcast, Clock, Pulse, TelevisionSimple } from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { NewsroomAdminNav } from "@/components/NewsroomAdminNav";
import { Button } from "@/components/ui/button";
import { usePageSeo } from "@/hooks/usePageSeo";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const defaultForm = {
  title: "",
  description: "",
  youtube_url: "",
  rtmp_url: "",
  event_type: "breaking update",
  coverage_focus: "",
  start_time: "",
  end_time: "",
  takeover_window_start: "",
  takeover_window_end: "",
  status: "scheduled",
  takeover_priority: 1,
  reporter_updates: "",
  reporters_on_scene: "",
  related_story_slugs: "",
  replay_title: "",
  is_homepage_featured: true,
};

const toDateTimeLocal = (value) => {
  if (!value) return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";
  const offsetDate = new Date(parsed.getTime() - parsed.getTimezoneOffset() * 60000);
  return offsetDate.toISOString().slice(0, 16);
};

const formatDateTime = (value) => {
  if (!value) return "No time set";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
};

export const AdminLivestreamsPage = () => {
  usePageSeo({ canonicalPath: "/admin/livestreams", description: "Manage Content Watch HQ livestreams.", robots: "noindex,nofollow", title: "Livestream Admin | Content Watch HQ" });
  const navigate = useNavigate();
  const [token] = useState(() => window.localStorage.getItem("content_watch_token"));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [streams, setStreams] = useState([]);
  const [form, setForm] = useState(defaultForm);
  const [editingStreamId, setEditingStreamId] = useState(null);

  const loadStreams = async () => {
    const response = await axios.get(`${API_BASE}/office/livestreams`, { headers: { Authorization: `Bearer ${token}` } });
    setStreams(response.data);
  };

  useEffect(() => {
    if (!token) {
      navigate("/newsroom-login");
      return;
    }
    axios.get(`${API_BASE}/auth/me`, { headers: { Authorization: `Bearer ${token}` } }).then(() => loadStreams()).catch(() => {
      window.localStorage.removeItem("content_watch_token");
      navigate("/newsroom-login");
    }).finally(() => setLoading(false));
  }, [navigate, token]);

  const liveCount = useMemo(() => streams.filter((stream) => stream.status === "live").length, [streams]);
  const scheduledCount = useMemo(() => streams.filter((stream) => stream.status === "scheduled").length, [streams]);
  const featuredStream = useMemo(() => streams.find((stream) => stream.is_homepage_featured) || null, [streams]);

  const updateField = (field) => (event) => {
    const value = field === "is_homepage_featured" ? event.target.checked : event.target.value;
    setForm((current) => ({ ...current, [field]: value }));
  };

  const resetForm = () => {
    setForm(defaultForm);
    setEditingStreamId(null);
  };

  const editStream = (stream) => {
    setEditingStreamId(stream.id);
    setForm({
      title: stream.title || "",
      description: stream.description || "",
      youtube_url: stream.youtube_url || "",
      rtmp_url: stream.rtmp_url || "",
      event_type: stream.event_type || "breaking update",
      coverage_focus: stream.coverage_focus || "",
      start_time: toDateTimeLocal(stream.start_time),
      end_time: toDateTimeLocal(stream.end_time),
      takeover_window_start: toDateTimeLocal(stream.takeover_window_start),
      takeover_window_end: toDateTimeLocal(stream.takeover_window_end),
      status: stream.status || "scheduled",
      takeover_priority: stream.takeover_priority || 1,
      reporter_updates: (stream.reporter_updates || []).join("\n"),
      reporters_on_scene: (stream.reporters_on_scene || []).join("\n"),
      related_story_slugs: (stream.related_story_slugs || []).join(", "),
      replay_title: stream.replay_title || "",
      is_homepage_featured: Boolean(stream.is_homepage_featured),
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      const payload = new FormData();
      Object.entries(form).forEach(([key, value]) => payload.append(key, `${value}`));
      if (editingStreamId) {
        await axios.put(`${API_BASE}/office/livestreams/${editingStreamId}`, payload, { headers: { Authorization: `Bearer ${token}` } });
        toast.success("Livestream updated.");
      } else {
        await axios.post(`${API_BASE}/office/livestreams`, payload, { headers: { Authorization: `Bearer ${token}` } });
        toast.success("Livestream saved.");
      }
      resetForm();
      await loadStreams();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Livestream could not be saved.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <main className="page-shell mx-auto max-w-7xl px-4 py-12 sm:px-6 sm:py-14 lg:px-8 lg:py-16" data-testid="admin-livestreams-page">
      <NewsroomAdminNav />

      <section className="border border-[#D9DCE1] bg-white p-6 shadow-[0_20px_60px_rgba(15,23,42,0.04)] sm:p-8" data-testid="admin-livestream-hero-panel">
        <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#D21404]">Livestream command</p>
        <h1 className="mt-3 font-[Bebas_Neue] text-5xl uppercase tracking-[0.04em] text-[#0A0A0A]">Schedule takeovers, live desks, and replay positioning</h1>
        <div className="mt-8 grid gap-4 sm:grid-cols-3" data-testid="admin-livestream-summary-grid">
          <article className="border border-[#D9DCE1] bg-[#F8F9FA] p-4" data-testid="admin-livestream-summary-live"><p className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500"><Pulse size={16} weight="fill" /> Live now</p><p className="mt-3 font-[Bebas_Neue] text-4xl uppercase tracking-[0.04em] text-[#0A0A0A]">{liveCount}</p></article>
          <article className="border border-[#D9DCE1] bg-[#F8F9FA] p-4" data-testid="admin-livestream-summary-scheduled"><p className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500"><Clock size={16} weight="fill" /> Scheduled</p><p className="mt-3 font-[Bebas_Neue] text-4xl uppercase tracking-[0.04em] text-[#0A0A0A]">{scheduledCount}</p></article>
          <article className="border border-[#D9DCE1] bg-[#F8F9FA] p-4" data-testid="admin-livestream-summary-featured"><p className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500"><TelevisionSimple size={16} weight="fill" /> Homepage featured</p><p className="mt-3 text-sm font-semibold uppercase tracking-[0.08em] text-[#0A0A0A]">{featuredStream?.title || "None selected"}</p></article>
        </div>
      </section>

      <div className="mt-8 grid gap-8 lg:grid-cols-[minmax(0,460px)_minmax(0,1fr)]">
        <form className="border border-[#D9DCE1] bg-white p-6 shadow-[0_20px_60px_rgba(15,23,42,0.04)] sm:p-8" data-testid="admin-livestream-form" onSubmit={handleSubmit}>
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#D21404]">Livestream manager</p>
              <h2 className="mt-3 font-[Bebas_Neue] text-4xl uppercase tracking-[0.04em] text-[#0A0A0A]">{editingStreamId ? "Edit livestream" : "Add livestream"}</h2>
            </div>
            {editingStreamId ? <Button className="rounded-none border border-[#0A0A0A] bg-transparent px-4 py-3 text-xs font-bold uppercase tracking-[0.18em] text-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white" data-testid="admin-livestream-cancel-edit-button" onClick={resetForm} type="button" variant="ghost">Cancel</Button> : null}
          </div>

          <div className="mt-8 grid gap-4">
            <input className="border border-[#D9DCE1] px-4 py-4 text-sm outline-none focus:border-[#1338BE]" data-testid="admin-livestream-input-title" onChange={updateField("title")} placeholder="Stream title" required value={form.title} />
            <textarea className="min-h-[7rem] border border-[#D9DCE1] px-4 py-4 text-sm outline-none focus:border-[#1338BE]" data-testid="admin-livestream-input-description" onChange={updateField("description")} placeholder="Description" value={form.description} />
            <div className="grid gap-4 sm:grid-cols-2">
              <input className="border border-[#D9DCE1] px-4 py-4 text-sm outline-none focus:border-[#1338BE]" data-testid="admin-livestream-input-youtube_url" onChange={updateField("youtube_url")} placeholder="YouTube URL" value={form.youtube_url} />
              <input className="border border-[#D9DCE1] px-4 py-4 text-sm outline-none focus:border-[#1338BE]" data-testid="admin-livestream-input-rtmp_url" onChange={updateField("rtmp_url")} placeholder="RTMP URL" value={form.rtmp_url} />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <input className="border border-[#D9DCE1] px-4 py-4 text-sm outline-none focus:border-[#1338BE]" data-testid="admin-livestream-input-event_type" onChange={updateField("event_type")} placeholder="Event type" value={form.event_type} />
              <input className="border border-[#D9DCE1] px-4 py-4 text-sm outline-none focus:border-[#1338BE]" data-testid="admin-livestream-input-coverage_focus" onChange={updateField("coverage_focus")} placeholder="Coverage focus" value={form.coverage_focus} />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <input className="border border-[#D9DCE1] px-4 py-4 text-sm outline-none focus:border-[#1338BE]" data-testid="admin-livestream-input-start_time" onChange={updateField("start_time")} type="datetime-local" value={form.start_time} />
              <input className="border border-[#D9DCE1] px-4 py-4 text-sm outline-none focus:border-[#1338BE]" data-testid="admin-livestream-input-end_time" onChange={updateField("end_time")} type="datetime-local" value={form.end_time} />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <input className="border border-[#D9DCE1] px-4 py-4 text-sm outline-none focus:border-[#1338BE]" data-testid="admin-livestream-input-takeover_window_start" onChange={updateField("takeover_window_start")} type="datetime-local" value={form.takeover_window_start} />
              <input className="border border-[#D9DCE1] px-4 py-4 text-sm outline-none focus:border-[#1338BE]" data-testid="admin-livestream-input-takeover_window_end" onChange={updateField("takeover_window_end")} type="datetime-local" value={form.takeover_window_end} />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <select className="border border-[#D9DCE1] px-4 py-4 text-sm outline-none focus:border-[#1338BE]" data-testid="admin-livestream-status-select" onChange={updateField("status")} value={form.status}>
                <option value="scheduled">Scheduled</option>
                <option value="live">Live</option>
                <option value="ended">Ended</option>
              </select>
              <input className="border border-[#D9DCE1] px-4 py-4 text-sm outline-none focus:border-[#1338BE]" data-testid="admin-livestream-input-takeover_priority" min="1" onChange={updateField("takeover_priority")} type="number" value={form.takeover_priority} />
            </div>
            <textarea className="min-h-[6rem] border border-[#D9DCE1] px-4 py-4 text-sm outline-none focus:border-[#1338BE]" data-testid="admin-livestream-input-reporter_updates" onChange={updateField("reporter_updates")} placeholder="Reporter updates (one per line)" value={form.reporter_updates} />
            <textarea className="min-h-[5rem] border border-[#D9DCE1] px-4 py-4 text-sm outline-none focus:border-[#1338BE]" data-testid="admin-livestream-input-reporters_on_scene" onChange={updateField("reporters_on_scene")} placeholder="Reporters on scene (one per line)" value={form.reporters_on_scene} />
            <input className="border border-[#D9DCE1] px-4 py-4 text-sm outline-none focus:border-[#1338BE]" data-testid="admin-livestream-input-related_story_slugs" onChange={updateField("related_story_slugs")} placeholder="Related story slugs, comma separated" value={form.related_story_slugs} />
            <input className="border border-[#D9DCE1] px-4 py-4 text-sm outline-none focus:border-[#1338BE]" data-testid="admin-livestream-input-replay_title" onChange={updateField("replay_title")} placeholder="Replay label" value={form.replay_title} />
            <label className="flex items-center gap-3 border border-[#D9DCE1] bg-[#F8F9FA] px-4 py-4 text-sm font-semibold text-slate-800" data-testid="admin-livestream-featured-toggle">
              <input checked={form.is_homepage_featured} onChange={updateField("is_homepage_featured")} type="checkbox" />
              Make this the homepage featured stream
            </label>
            <Button className="rounded-none bg-[#1338BE] px-5 py-6 text-xs font-bold uppercase tracking-[0.2em] text-white hover:bg-[#102F9B]" data-testid="admin-livestream-submit-button" disabled={saving} type="submit">
              {saving ? (editingStreamId ? "Updating" : "Saving") : (editingStreamId ? "Update Livestream" : "Save Livestream")}
            </Button>
          </div>
        </form>

        <section className="border border-[#D9DCE1] bg-white p-6 shadow-[0_20px_60px_rgba(15,23,42,0.04)] sm:p-8" data-testid="admin-livestream-list-panel">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#3BB143]">Current livestreams</p>
              <h2 className="mt-3 font-[Bebas_Neue] text-4xl uppercase tracking-[0.04em] text-[#0A0A0A]">Coverage lineup</h2>
            </div>
            <Button className="rounded-none border border-[#0A0A0A] bg-transparent px-4 py-3 text-xs font-bold uppercase tracking-[0.18em] text-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white" data-testid="admin-livestream-refresh-button" onClick={() => loadStreams()} type="button" variant="ghost">Refresh lineup</Button>
          </div>
          {loading ? <p className="mt-4 text-sm text-slate-600">Loading livestreams…</p> : null}
          <div className="mt-8 space-y-5" data-testid="admin-livestream-list">
            {streams.map((stream) => (
              <article key={stream.id} className="border border-[#D9DCE1] bg-[#F8F9FA] p-5 sm:p-6" data-testid={`admin-livestream-card-${stream.slug}`}>
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <p className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.18em] text-[#1338BE]"><Broadcast size={16} weight="fill" /> {stream.status}{stream.is_homepage_featured ? " · featured" : ""}</p>
                    <h3 className="mt-2 text-lg font-bold uppercase tracking-[0.08em] text-[#0A0A0A]">{stream.title}</h3>
                  </div>
                  <Button className="rounded-none border border-[#0A0A0A] bg-transparent px-4 py-3 text-xs font-bold uppercase tracking-[0.18em] text-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white" data-testid={`admin-livestream-edit-button-${stream.slug}`} onClick={() => editStream(stream)} type="button" variant="ghost">Edit</Button>
                </div>
                <p className="mt-3 text-sm leading-7 text-slate-700">{stream.description || "No description yet."}</p>
                <div className="mt-4 grid gap-3 sm:grid-cols-2" data-testid={`admin-livestream-card-meta-${stream.slug}`}>
                  <div className="border border-[#D9DCE1] bg-white p-3"><p className="text-[11px] font-bold uppercase tracking-[0.16em] text-slate-500">Takeover window</p><p className="mt-2 text-sm font-semibold text-slate-900">{formatDateTime(stream.takeover_window_start)} → {formatDateTime(stream.takeover_window_end)}</p></div>
                  <div className="border border-[#D9DCE1] bg-white p-3"><p className="text-[11px] font-bold uppercase tracking-[0.16em] text-slate-500">Priority / replay</p><p className="mt-2 text-sm font-semibold text-slate-900">P{stream.takeover_priority || 1} · {stream.replay_title || "No replay label"}</p></div>
                </div>
                {(stream.reporters_on_scene || []).length ? <p className="mt-4 text-xs uppercase tracking-[0.16em] text-slate-500" data-testid={`admin-livestream-reporters-${stream.slug}`}>On scene: {(stream.reporters_on_scene || []).join(", ")}</p> : null}
              </article>
            ))}
          </div>
        </section>
      </div>
    </main>
  );
};